/**
 * MySummaryPanel — Dashboard recap card with 4 tabs (Marcadores · Torneos · Ligas · Inscripciones)
 * - Jugador/Profesor/Directivo: 100 items per tab.
 * - Admin: 1000 items per tab (all-app view).
 * Items are clickable links to the corresponding detail page.
 */
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { TennisBall, Trophy, Medal, ClipboardText, MagnifyingGlass } from "@phosphor-icons/react";

const TABS = [
  { id: "marcadores",    label: "Marcadores",    icon: TennisBall,    color: "#CCFF00", route: "/dashboard/marcador" },
  { id: "torneos",       label: "Torneos",       icon: Trophy,        color: "#FFB800", route: "/dashboard/torneos" },
  { id: "ligas",         label: "Ligas",         icon: Medal,         color: "#FF8A00", route: "/dashboard/ligas" },
  { id: "inscripciones", label: "Inscripciones", icon: ClipboardText, color: "#34C759", route: null },
];

const STATUS_COLORS = {
  validado: "text-green-400",
  finalizado: "text-blue-400",
  pendiente_validacion: "text-orange-400",
  en_curso: "text-[#CCFF00]",
  abierto: "text-green-400",
  cerrado: "text-gray-400",
  programado: "text-cyan-400",
  cancelado: "text-red-400",
};

export default function MySummaryPanel() {
  const { user } = useAuth();
  const [active, setActive] = useState("marcadores");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const isAdmin = user?.role === "admin";
  const limit = isAdmin ? 100000 : 1000;

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard/my-summary")
      .then(({ data }) => setData(data))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, []);

  const items = data[active] || [];
  const total = data[`${active}_total`] ?? items.length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = [it.label, it.date, it.status, it.category, it.court, it.extra, it.kind, it.format]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  const tabActive = TABS.find((t) => t.id === active);

  return (
    <div className="card-tactical p-5" data-testid="my-summary-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] font-bold mb-0.5">Mi Resumen</p>
          <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase tracking-tight">
            Marcadores · Torneos · Ligas · Inscripciones
          </h2>
          <p className="text-[11px] text-gray-500 mt-1">
            {isAdmin ? "Vista global · hasta 100 000 por categoría" : "Tus últimos 1 000 registros por categoría"}
          </p>
        </div>
        <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px]">
          Tope: {limit}
        </Badge>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4" data-testid="summary-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count = data[`${t.id}_total`] ?? (data[t.id]?.length || 0);
          const on = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => { setActive(t.id); setSearch(""); }}
              className={`flex items-center gap-2 px-3 py-2.5 border transition-all text-left ${on ? "ring-2" : "opacity-70 hover:opacity-100"}`}
              style={{
                borderColor: on ? t.color : "rgba(255,255,255,0.1)",
                backgroundColor: on ? `${t.color}15` : "#0A0A0A",
                boxShadow: on ? `0 0 0 1px ${t.color}` : undefined,
              }}
              data-testid={`summary-tab-${t.id}`}
            >
              <Icon size={18} weight="duotone" style={{ color: t.color }} />
              <div className="min-w-0 flex-1">
                <p className="text-[9px] uppercase tracking-widest text-gray-500">{t.label}</p>
                <p className="font-['Barlow_Condensed'] font-black text-lg leading-none mt-0.5" style={{ color: on ? t.color : "#FFF" }}>
                  {count}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Buscar en ${tabActive?.label.toLowerCase()}…`}
          className="bg-[#0A0A0A] border-white/10 text-white pl-9 h-9 text-sm"
          data-testid={`summary-search-${active}`}
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs">✕</button>
        )}
      </div>

      {/* List */}
      <div className="max-h-[420px] overflow-y-auto pr-1" data-testid={`summary-list-${active}`}>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 border-2 border-[#CCFF00] border-t-transparent animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8" data-testid="summary-empty">
            {search ? "Sin resultados con ese término" : "Nada por aquí todavía"}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {filtered.map((it, i) => {
              const sc = STATUS_COLORS[it.status] || "text-gray-400";
              const route = (() => {
                if (active === "marcadores") return `/dashboard/marcador/${it.id}`;
                if (active === "torneos") return "/dashboard/torneos";
                if (active === "ligas") return "/dashboard/ligas";
                if (active === "inscripciones") {
                  if (it.kind === "torneo") return "/dashboard/torneos";
                  if (it.kind === "reta") return "/dashboard/retas";
                  if (it.kind === "renta") return "/dashboard/canchas";
                }
                return "#";
              })();
              return (
                <li key={`${active}-${it.id || i}`}>
                  <Link
                    to={route}
                    className="flex items-center gap-3 p-2.5 bg-[#0A0A0A] border border-white/5 hover:border-[#CCFF00]/30 hover:bg-[#CCFF00]/5 transition-colors group"
                    data-testid={`summary-item-${active}-${i}`}
                  >
                    <span className="text-[10px] tabular-nums text-gray-500 font-['JetBrains_Mono'] w-8 text-center shrink-0">
                      #{i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-semibold truncate group-hover:text-[#CCFF00]">
                        {it.label || "Sin nombre"}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                        {it.date && <span className="text-[10px] text-gray-500">{it.date}</span>}
                        {it.status && <span className={`text-[10px] uppercase font-bold tracking-wider ${sc}`}>{it.status}</span>}
                        {it.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[9px] px-1 py-0">Cat. {it.category}</Badge>}
                        {it.court && <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[9px] px-1 py-0">🎾 {it.court}</Badge>}
                        {it.kind && active === "inscripciones" && <Badge variant="outline" className="bg-orange-500/10 text-orange-300 border-orange-500/30 text-[9px] uppercase px-1 py-0">{it.kind}</Badge>}
                        {active === "marcadores" && it.is_mine === false && <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[9px] px-1 py-0">Solo admin</Badge>}
                      </div>
                    </div>
                    {it.result && (
                      <span className="font-['Barlow_Condensed'] font-black text-lg text-white tabular-nums shrink-0">
                        {it.result}
                      </span>
                    )}
                    {it.extra && !it.result && (
                      <span className="text-[10px] text-gray-500 shrink-0 max-w-[120px] truncate">{it.extra}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer count */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 text-[10px] uppercase tracking-widest text-gray-500">
        <span>
          Mostrando <span className="text-white font-semibold">{filtered.length}</span> de {total}
        </span>
        {tabActive?.route && (
          <Link to={tabActive.route} className="text-[#CCFF00] hover:underline" data-testid={`summary-go-${active}`}>
            Abrir {tabActive.label} →
          </Link>
        )}
      </div>
    </div>
  );
}
